import React, { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { AnimatedSection } from "@/components/site/Motion";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  ExternalLink,
  MapPin,
  Mail,
  Phone,
  Search,
  Bookmark,
} from "lucide-react";
import {
  brand,
  company,
  services,
  industries,
  solutions,
  insights,
} from "@/mock/mock";
import { PortfolioCatalog } from "@/components/catalog/PortfolioCatalog";
import productsCatalogData from "@/data/products.json";
import projectsCatalogData from "@/data/projects.json";
import { hasLiveDemoUrl } from "@/lib/catalogDetail";

const PageShell = ({ title, subtitle, subtitleClassName, children, cta, hideHeader = false }) => (
  <div className="mx-auto max-w-6xl px-4 py-10 lg:py-14">
    <div className="relative min-w-0 max-w-full overflow-hidden rounded-[28px]">
      <div aria-hidden="true" className="pointer-events-none absolute inset-0">
        <div className="absolute -top-28 left-1/2 h-[420px] w-[820px] -translate-x-1/2 rounded-full bg-[radial-gradient(circle_at_30%_30%,rgba(255,145,56,0.16),transparent_60%)] blur-3xl" />
        <div className="absolute -top-20 -right-40 h-[360px] w-[360px] rounded-full bg-[radial-gradient(circle_at_40%_40%,rgba(255,145,56,0.10),transparent_62%)] blur-3xl" />
      </div>

      <div className="relative flex min-w-0 max-w-full flex-col gap-6">
        {!hideHeader ? (
          <>
            <AnimatedSection as="div" delay={0} className="flex items-start justify-between gap-6 flex-wrap py-6">
              <div className="max-w-3xl">
                <div className="text-xs font-semibold tracking-widest text-primary/90">{brand.name}</div>
                <h1 className="mt-2 text-3xl sm:text-4xl font-semibold tracking-tight">{title}</h1>
                {subtitle ? (
                  <p className={cn("mt-3 text-sm sm:text-base text-muted-foreground", subtitleClassName)}>
                    {subtitle}
                  </p>
                ) : null}
              </div>
              {cta ? <div className="flex items-center gap-2">{cta}</div> : null}
            </AnimatedSection>

            <Separator className="border-border/70" />
          </>
        ) : null}

        <AnimatedSection as="div" delay={120} threshold={0}>
          {children}
        </AnimatedSection>
      </div>
    </div>
  </div>
);

const LocalStore = {
  read(key, fallback) {
    try {
      return JSON.parse(localStorage.getItem(key) || JSON.stringify(fallback));
    } catch {
      return fallback;
    }
  },
  write(key, value) {
    localStorage.setItem(key, JSON.stringify(value));
  },
};

export const CompanyPage = () => (
  <PageShell
    title="Company"
    subtitle="Enterprise credibility with startup agility - built into our delivery culture."
    cta={
      <>
        <Button asChild variant="outline" className="bg-background/60">
          <Link to="/contact">Talk to an Expert</Link>
        </Button>
        <Button asChild>
          <Link to="/projects">View Projects</Link>
        </Button>
      </>
    }
  >
    <div className="grid gap-6 lg:grid-cols-12">
      <Card className="lg:col-span-7 border-border/70 bg-background/60 backdrop-blur-[16px]">
        <CardHeader>
          <CardTitle>{company.about.title}</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground grid gap-3">
          {company.about.body.map((p) => (
            <p key={p}>{p}</p>
          ))}
          <div className="mt-2 flex flex-wrap gap-2">
            {company.about.focus.map((f) => (
              <Badge key={f} variant="secondary" className="bg-secondary/60">
                {f}
              </Badge>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card
        id="leadership"
        className="lg:col-span-5 border-border/70 bg-background/60 backdrop-blur-[16px]"
      >
        <CardHeader>
          <CardTitle>Leadership</CardTitle>
        </CardHeader>
        <CardContent className="text-sm">
          <div className="grid gap-3">
            {company.leadership.map((l) => (
              <div key={l.name} className="rounded-xl border border-border/60 bg-background/60 p-3">
                <div className="font-medium">{l.name}</div>
                <div className="mt-1 text-muted-foreground">{l.bio}</div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>

    <div className="mt-6 grid gap-6 lg:grid-cols-12">
      <Card className="lg:col-span-7 border-border/70 bg-background/60 backdrop-blur-[16px]">
        <CardHeader>
          <CardTitle>Vision & Values</CardTitle>
        </CardHeader>
        <CardContent>
          <Accordion type="single" collapsible className="w-full">
            {company.values.map((v, idx) => (
              <AccordionItem key={v.title} value={`v-${idx}`} className="border-border/60">
                <AccordionTrigger className="text-left">{v.title}</AccordionTrigger>
                <AccordionContent className="text-muted-foreground">{v.detail}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </CardContent>
      </Card>

      <Card
        id="engagement"
        className="lg:col-span-5 border-border/70 bg-background/60 backdrop-blur-[16px]"
      >
        <CardHeader>
          <CardTitle>Engagement Models</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground grid gap-3">
          {company.engagementModels.map((m) => (
            <div key={m.title} className="rounded-xl border border-border/60 bg-background/60 p-3">
              <div className="font-medium text-foreground">{m.title}</div>
              <div className="mt-1">{m.detail}</div>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>

    <div className="mt-6 grid gap-6 lg:grid-cols-12">
      <Card
        id="compliance"
        className="lg:col-span-12 border-border/70 bg-background/60 backdrop-blur-[16px]"
      >
        <CardHeader>
          <CardTitle>Legal & Compliance</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-3">
          <div className="text-sm text-muted-foreground">
            Enterprise-friendly engagement: NDA-ready, secure SDLC practices, and auditability patterns.
          </div>
          <div className="flex flex-wrap gap-2">
            {company.compliance.map((c) => (
              <Badge key={c} variant="outline" className="bg-background/60">
                <CheckCircle2 className="h-3.5 w-3.5 text-primary" /> {c}
              </Badge>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  </PageShell>
);

const ListGrid = ({ items, basePath, label, getMeta }) => (
  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
    {items.map((it) => {
      const meta = getMeta?.(it);
      return (
        <Card
          key={it.slug}
          className="group relative overflow-hidden border-border/70 bg-background/60 backdrop-blur-[16px] transition-[transform,box-shadow,border-color] duration-300 hover:-translate-y-0.5 hover:shadow-lg hover:border-border"
        >
          <div className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
            <div className="absolute -top-16 -right-24 h-[220px] w-[220px] rounded-full bg-[radial-gradient(circle_at_30%_30%,rgba(255,145,56,0.14),transparent_62%)] blur-3xl" />
          </div>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between gap-3">
              <CardTitle className="text-lg">{it.title}</CardTitle>
              {meta?.badge ? (
                <Badge variant={meta.badgeVariant || "secondary"} className={meta.badgeClassName}>
                  {meta.badge}
                </Badge>
              ) : null}
            </div>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground">
            {it.summary || it.subtitle || it.excerpt || ""}
            {meta?.chips?.length ? (
              <div className="mt-4 flex flex-wrap gap-2">
                {meta.chips.map((c) => (
                  <Badge key={c} variant="outline" className="bg-background/50">
                    {c}
                  </Badge>
                ))}
              </div>
            ) : null}
          </CardContent>
          <CardFooter className="pt-0">
            <Button asChild variant="link" className="px-0 text-primary">
              <Link to={`${basePath}/${it.slug}`}>
                View {label} <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
          </CardFooter>
        </Card>
      );
    })}
  </div>
);

export const ServicesPage = () => (
  <PageShell
    title="Services"
    subtitle="Each service page follows an enterprise layout: overview, problems we solve, capabilities, stack, use cases, and a clear CTA."
    cta={
      <Button asChild>
        <Link to="/contact">Start a Conversation</Link>
      </Button>
    }
  >
    <ListGrid
      items={services}
      basePath="/services"
      label="service"
      getMeta={(s) => ({ chips: s.stack?.slice(0, 4), badge: "Capability", badgeClassName: "bg-secondary/60" })}
    />
  </PageShell>
);

export const IndustriesPage = () => (
  <PageShell
    title="Industries"
    subtitle="Industry pages highlight key challenges, our solutions, and sample use cases."
    cta={
      <Button asChild variant="outline" className="bg-background/60">
        <Link to="/contact">Talk to Sales</Link>
      </Button>
    }
  >
    <ListGrid items={industries} basePath="/industries" label="industry" getMeta={(i) => ({ badge: "Industry", badgeClassName: "bg-secondary/60" })} />
  </PageShell>
);

export const SolutionsPage = () => (
  <PageShell
    title="Solutions"
    subtitle="Packaged offerings for faster sales conversations - ideal for non-technical buyers."
    cta={
      <>
        <Button asChild variant="outline" className="bg-background/60">
          <Link to="/services">View Services</Link>
        </Button>
        <Button asChild>
          <Link to="/contact">Get a Proposal</Link>
        </Button>
      </>
    }
  >
    <ListGrid items={solutions} basePath="/solutions" label="solution" getMeta={(s) => ({ badge: "Solution", badgeClassName: "bg-secondary/60", chips: s.bullets?.slice(0, 3) })} />
  </PageShell>
);

export const ProductsPage = () => (
  <PortfolioCatalog
    items={productsCatalogData}
    theme="orange"
    eyebrow="Product catalog"
    titleLine={(n) => `${n === 1 ? "Live product" : "Live products"} to explore`}
    idleLabel="Coming soon"
    kind="product"
  />
);

export const ProjectsPage = () => (
  <PortfolioCatalog
    items={projectsCatalogData}
    theme="orange"
    eyebrow="Project showcase"
    titleLine={(n) => (n === 1 ? "Project" : "Projects")}
    idleLabel="Coming soon"
    kind="project"
  />
);

const ellipsonicHours = [
  "Building real products",
  "Solving real customer problems",
  "Shipping outcomes, not sitting through meetings",
  "Taking full ownership of what you commit to",
];

const creationTime = [
  {
    title: "Build products",
    detail: "Tools, platforms, and experiments that can evolve into real offerings.",
  },
  {
    title: "Build businesses",
    detail: "Startups, freelance practices, or independent ventures.",
  },
  {
    title: "Go deep into mastery",
    detail: "Technology, design, systems thinking, or any craft worth pursuing.",
  },
  {
    title: "Create and express",
    detail: "Content, writing, filmmaking, teaching, or knowledge-sharing.",
  },
  {
    title: "Contribute to society",
    detail: "Mentoring, open-source work, education initiatives, or community problem-solving.",
  },
];

const accountabilityPrinciples = [
  "This is not a low-effort setup",
  "This is not about doing just enough",
  "This is not an escape from responsibility",
];

const highAccountability = [
  "Output matters",
  "Ownership is expected",
  "Freedom is earned and sustained through performance",
];

const worksFor = [
  "Want to build, not just have a job",
  "Take responsibility for their own growth",
  "Have, or want, meaningful projects beyond a single role",
  "Believe work should support life, not replace it",
];

export const CareersPage = () => (
  <PageShell title={null} subtitle={null} cta={null} hideHeader>
    <div className="relative grid min-w-0 max-w-full gap-10 overflow-hidden pt-2">
      <section className="mx-auto w-full max-w-5xl min-w-0 text-center">
        <div className="inline-flex items-center rounded-full bg-primary/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.28em] text-primary">
          Careers at Ellipsonic
        </div>
        <h2 className="mx-auto mt-5 max-w-[320px] text-2xl font-semibold leading-tight tracking-tight text-foreground sm:max-w-none sm:text-5xl lg:text-6xl">
          <span className="block">The 30-Hour Work Week</span>
          <span className="block text-primary italic">(By Design)</span>
        </h2>
        <p className="mx-auto mt-6 max-w-[320px] text-sm leading-6 text-muted-foreground sm:max-w-3xl sm:text-lg">
          At Ellipsonic, 30 hours does not mean working less. It means working with clarity,
          ownership, and intensity while refusing to waste life in low-value busyness.
        </p>
        <div className="mx-auto mt-6 flex max-w-xs flex-wrap justify-center gap-2 sm:max-w-none">
          <Badge variant="secondary" className="bg-secondary/60">Clarity</Badge>
          <Badge variant="secondary" className="bg-secondary/60">Ownership</Badge>
          <Badge variant="secondary" className="bg-secondary/60">Intensity</Badge>
          <Badge variant="secondary" className="bg-secondary/60">Outcomes</Badge>
        </div>
      </section>

      <section className="relative min-w-0 max-w-full overflow-hidden rounded-[34px] bg-slate-950 p-8 text-white shadow-2xl sm:p-12 lg:p-16">
        <div aria-hidden="true" className="pointer-events-none absolute inset-0">
          <div className="absolute -top-24 right-[-10%] h-[360px] w-[360px] rounded-full bg-primary/20 blur-3xl" />
          <div className="absolute bottom-[-30%] left-[-10%] h-[420px] w-[420px] rounded-full bg-white/10 blur-3xl" />
        </div>
        <div className="relative grid gap-10 lg:grid-cols-12 lg:items-center">
          <div className="lg:col-span-5">
            <div className="text-6xl font-semibold tracking-tight text-primary sm:text-7xl">30</div>
            <div className="mt-2 text-2xl font-semibold tracking-tight sm:text-4xl">
              deep-focus Ellipsonic hours
            </div>
            <p className="mt-5 text-sm leading-6 text-slate-300 sm:text-base">
              We care about what gets built, not how long someone stays online.
            </p>
          </div>
          <div className="lg:col-span-7">
            <div className="grid gap-3 sm:grid-cols-2">
              {ellipsonicHours.map((item) => (
                <div key={item} className="rounded-2xl border border-white/10 bg-white/5 p-5 backdrop-blur">
                  <CheckCircle2 className="h-5 w-5 text-primary" />
                  <div className="mt-4 text-sm font-medium text-white">{item}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="grid gap-6 rounded-[34px] border border-border/70 bg-background/60 p-6 shadow-xl backdrop-blur-[16px] sm:p-8 lg:grid-cols-12 lg:items-center lg:p-10">
        <div className="lg:col-span-4">
          <div className="text-xs font-semibold uppercase tracking-[0.28em] text-primary">Culture primer</div>
          <h3 className="mt-3 text-2xl font-semibold tracking-tight sm:text-3xl">First, watch this</h3>
          <p className="mt-4 text-sm leading-6 text-muted-foreground sm:text-base">
            Before this sounds like just another flexible job, this short film captures why
            we care about not letting work consume the whole person.
          </p>
        </div>
        <div className="lg:col-span-8">
          <div className="relative aspect-video overflow-hidden rounded-[26px] border border-border/70 bg-slate-950 shadow-2xl">
            <iframe
              className="absolute inset-0 h-full w-full"
              src="https://www.youtube-nocookie.com/embed/PAK1VcjHD3A"
              title="FULL TIME | Omeleto"
              loading="lazy"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              referrerPolicy="strict-origin-when-cross-origin"
              allowFullScreen
            />
          </div>
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-12 lg:items-start">
        <div className="lg:col-span-4">
          <div className="sticky top-24">
            <div className="text-xs font-semibold uppercase tracking-[0.28em] text-primary">Outside the 30</div>
            <h3 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">Creation time</h3>
            <p className="mt-4 text-sm leading-6 text-muted-foreground sm:text-base">
              The remaining time is not simply free time. It is intentional space for the
              work, learning, and contribution that expands a person beyond a single role.
            </p>
          </div>
        </div>
        <div className="lg:col-span-8">
          <div className="grid gap-4 sm:grid-cols-2">
            {creationTime.map((item, index) => (
              <div
                key={item.title}
                className={index === 4 ? "rounded-[26px] border border-border/70 bg-background/60 p-6 shadow-sm backdrop-blur-[16px] sm:col-span-2" : "rounded-[26px] border border-border/70 bg-background/60 p-6 shadow-sm backdrop-blur-[16px]"}
              >
                <div className="text-xs font-semibold tracking-[0.18em] text-primary">0{index + 1}</div>
                <div className="mt-3 text-xl font-semibold tracking-tight">{item.title}</div>
                <p className="mt-2 text-sm leading-6 text-muted-foreground">{item.detail}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="rounded-[34px] border border-border/70 bg-background/60 p-8 shadow-xl backdrop-blur-[16px] sm:p-12">
        <div className="grid gap-10 lg:grid-cols-12 lg:items-center">
          <div className="lg:col-span-5">
            <h3 className="text-3xl font-semibold tracking-tight sm:text-4xl">Value beyond the company</h3>
            <p className="mt-4 text-sm leading-6 text-muted-foreground sm:text-base">
              A strong professional is not someone who only delivers inside a company.
              It is someone who creates value beyond it.
            </p>
          </div>
          <div className="lg:col-span-7">
            <div className="rounded-[26px] bg-secondary/60 p-6">
              <div className="text-sm font-semibold text-foreground">Ideas that start here can become:</div>
              <ul className="mt-5 grid gap-3 text-sm text-muted-foreground sm:grid-cols-2">
                {["Internal products", "Spin-off ventures", "Ecosystem collaborations", "Sharper individuals with broader impact"].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-[30px] border border-border/70 bg-background/60 p-8 shadow-sm backdrop-blur-[16px]">
          <div className="text-xs font-semibold uppercase tracking-[0.28em] text-primary">Clear boundary</div>
          <h3 className="mt-3 text-2xl font-semibold tracking-tight">This is not a part-time job</h3>
          <div className="mt-6 grid gap-3">
            {accountabilityPrinciples.map((item) => (
              <div key={item} className="rounded-2xl border border-border/70 bg-background/70 p-4 text-sm font-medium">
                {item}
              </div>
            ))}
          </div>
        </div>
        <div className="rounded-[30px] bg-primary p-8 text-white shadow-lg shadow-primary/20">
          <div className="text-xs font-semibold uppercase tracking-[0.28em] text-white/70">High accountability</div>
          <h3 className="mt-3 text-2xl font-semibold tracking-tight">Freedom only works with performance</h3>
          <ul className="mt-6 grid gap-3 text-sm text-white/90">
            {highAccountability.map((item) => (
              <li key={item} className="flex items-start gap-3 rounded-2xl bg-white/10 p-4">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="rounded-[34px] bg-slate-950 p-8 text-white sm:p-12 lg:p-14">
        <div className="grid gap-10 lg:grid-cols-12 lg:items-start">
          <div className="lg:col-span-5">
            <div className="text-xs font-semibold uppercase tracking-[0.28em] text-primary">Why it works</div>
            <h3 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
              We are not optimizing for hours
            </h3>
            <p className="mt-5 text-sm leading-6 text-slate-300 sm:text-base">
              Traditional full-time roles often consume time, attention, and energy,
              leaving little room to build anything of your own. We chose a different model.
            </p>
          </div>
          <div className="lg:col-span-7">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="rounded-2xl border border-white/10 bg-white/5 p-6">
                <div className="text-sm font-semibold text-primary">Inside Ellipsonic</div>
                <p className="mt-3 text-sm leading-6 text-slate-300">
                  Deliver meaningful work through focused product thinking, customer problem-solving,
                  and commitments you fully own.
                </p>
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/5 p-6">
                <div className="text-sm font-semibold text-primary">Beyond Ellipsonic</div>
                <p className="mt-3 text-sm leading-6 text-slate-300">
                  Keep the space to build, explore, master your craft, and contribute at a larger level.
                </p>
              </div>
            </div>
            <div className="mt-6 rounded-2xl border border-white/10 bg-white/5 p-6 text-lg font-semibold tracking-tight">
              We are building a company, not a cage.
            </div>
          </div>
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-12 lg:items-center">
        <div className="lg:col-span-7">
          <h3 className="text-3xl font-semibold tracking-tight sm:text-4xl">Who this works for</h3>
          <p className="mt-4 text-sm leading-6 text-muted-foreground sm:text-base">
            This model is for builders who want their work to support a fuller life,
            not replace it.
          </p>
          <div className="mt-6 grid gap-3 sm:grid-cols-2">
            {worksFor.map((item) => (
              <div key={item} className="flex items-start gap-3 rounded-2xl border border-border/70 bg-background/60 p-4 text-sm font-medium shadow-sm backdrop-blur-[16px]">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                <span>{item}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="lg:col-span-5">
          <div className="rounded-[30px] border border-border/70 bg-background/60 p-8 shadow-sm backdrop-blur-[16px]">
            <div className="text-xl font-semibold tracking-tight">Want to talk?</div>
            <p className="mt-3 text-sm leading-6 text-muted-foreground">
              If this model sounds like the way you want to build, reach out with what you
              are good at, what you are building, and what you want to own.
            </p>
            <div className="mt-6 flex flex-col gap-3 sm:flex-row lg:flex-col">
              <Button asChild size="lg">
                <a href={`mailto:${brand.contact.emails[1]}?subject=Careers%20at%20Ellipsonic`}>
                  Email Careers <ArrowRight className="h-4 w-4" />
                </a>
              </Button>
              <Button asChild size="lg" variant="outline" className="bg-background/60">
                <Link to="/contact">Ask a question</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  </PageShell>
);

const ContactSchema = z.object({
  name: z.string().min(2, "Name is required"),
  email: z.string().email("Valid email required"),
  company: z.string().optional(),
  message: z.string().min(10, "Please add a short message"),
});

export const ContactPage = () => {
  const form = useForm({ resolver: zodResolver(ContactSchema), defaultValues: { name: "", email: "", company: "", message: "" } });

  const onSubmit = (values) => {
    const key = "ellipsonic_contact_submissions";
    const existing = LocalStore.read(key, []);
    const next = [{ id: crypto.randomUUID(), createdAt: new Date().toISOString(), ...values }, ...existing];
    LocalStore.write(key, next);
    toast({ title: "Message sent", description: "Saved locally for now (MOCK). We'll wire backend email/CRM next." });
    form.reset();
  };

  return (
    <PageShell
      title="Contact"
      subtitle="Tell us what you're building. We'll come back with a clear plan and a practical next step."
      cta={
        <Button asChild variant="outline" className="bg-background/60">
          <a href={`mailto:${brand.contact.emails[0]}`}>Email us</a>
        </Button>
      }
    >
      <div className="grid gap-6 lg:grid-cols-12">
        <Card className="lg:col-span-5 border-border/70 bg-background/60 backdrop-blur-[16px]">
          <CardHeader>
            <CardTitle>Reach us</CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground grid gap-4">
            <div className="flex items-start gap-2">
              <MapPin className="h-4 w-4 mt-0.5" />
              <span>{brand.contact.address}</span>
            </div>
            <div className="flex items-center gap-2">
              <Phone className="h-4 w-4" />
              <a className="hover:text-foreground transition-colors" href={`tel:${brand.contact.phone.replace(/\s/g, "")}`}>
                {brand.contact.phone}
              </a>
            </div>
            <div className="flex items-center gap-2">
              <Mail className="h-4 w-4" />
              <a className="hover:text-foreground transition-colors" href={`mailto:${brand.contact.emails[0]}`}>
                {brand.contact.emails[0]}
              </a>
            </div>
            <div className="flex items-center gap-2">
              <Mail className="h-4 w-4" />
              <a className="hover:text-foreground transition-colors" href={`mailto:${brand.contact.emails[1]}`}>
                {brand.contact.emails[1]}
              </a>
            </div>

            <Separator className="my-2" />

            <div className="rounded-xl border border-border/60 bg-background/60 p-3">
              <div className="text-xs font-semibold">Meeting link</div>
              <div className="mt-1 text-xs text-muted-foreground">
                If you share a Calendly/meeting URL, we'll place it here.
              </div>
              <Button
                type="button"
                variant="link"
                className="px-0 text-primary"
                onClick={() => toast({ title: "Add your Calendly", description: "Send a meeting link and we'll wire it in." })}
              >
                Add meeting link <ExternalLink className="h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="lg:col-span-7 border-border/70 bg-background/60 backdrop-blur-[16px]">
          <CardHeader>
            <CardTitle>Start a conversation</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={form.handleSubmit(onSubmit)} className="grid gap-3">
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <div className="text-xs font-medium">Name</div>
                  <Input className="mt-1 bg-background/70" {...form.register("name")} />
                  {form.formState.errors.name ? (
                    <div className="mt-1 text-xs text-destructive">{form.formState.errors.name.message}</div>
                  ) : null}
                </div>
                <div>
                  <div className="text-xs font-medium">Email</div>
                  <Input className="mt-1 bg-background/70" {...form.register("email")} />
                  {form.formState.errors.email ? (
                    <div className="mt-1 text-xs text-destructive">{form.formState.errors.email.message}</div>
                  ) : null}
                </div>
              </div>
              <div>
                <div className="text-xs font-medium">Company (optional)</div>
                <Input className="mt-1 bg-background/70" {...form.register("company")} />
              </div>
              <div>
                <div className="text-xs font-medium">Message</div>
                <Textarea className="mt-1 min-h-[150px] bg-background/70" {...form.register("message")} />
                {form.formState.errors.message ? (
                  <div className="mt-1 text-xs text-destructive">{form.formState.errors.message.message}</div>
                ) : null}
              </div>
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="text-xs text-muted-foreground">
                  We usually respond within 1-2 business days.
                </div>
                <Button type="submit" size="lg">Send message</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </PageShell>
  );
};

export const InsightsPage = () => {
  const [active, setActive] = useState("All");
  const [q, setQ] = useState("");

  const posts = useMemo(() => {
    const query = q.trim().toLowerCase();
    return insights.posts
      .filter((p) => (active === "All" ? true : p.category === active))
      .filter((p) =>
        query
          ? [p.title, p.excerpt, p.category].join(" ").toLowerCase().includes(query)
          : true
      )
      .sort((a, b) => (a.date < b.date ? 1 : -1));
  }, [active, q]);

  return (
    <PageShell
      title="Insights"
      subtitle="Thought leadership: AI in business, cloud best practices, engineering culture, and startup tech lessons."
      cta={
        <Button asChild variant="outline" className="bg-background/60">
          <Link to="/contact">Talk to an Expert</Link>
        </Button>
      }
    >
      <div className="grid gap-6">
        <div className="flex flex-col lg:flex-row gap-4 lg:items-center lg:justify-between">
          <Tabs value={active} onValueChange={setActive} className="w-full lg:w-auto">
            <TabsList className="bg-secondary/60">
              <TabsTrigger value="All">All</TabsTrigger>
              {insights.categories.map((c) => (
                <TabsTrigger key={c} value={c}>
                  {c}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>

          <div className="relative max-w-md w-full">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search insights" className="pl-9 bg-background/70" />
          </div>
        </div>

        <div className="grid gap-4 lg:grid-cols-3">
          {posts.map((p) => (
            <PostCard key={p.slug} post={p} />
          ))}
        </div>
      </div>
    </PageShell>
  );
};

const PostCard = ({ post }) => {
  const key = "ellipsonic_bookmarks";
  const [bookmarks, setBookmarks] = useState(() => LocalStore.read(key, {}));
  const bookmarked = Boolean(bookmarks[post.slug]);

  const toggle = () => {
    const next = { ...bookmarks, [post.slug]: !bookmarked };
    if (!next[post.slug]) delete next[post.slug];
    setBookmarks(next);
    LocalStore.write(key, next);
    toast({ title: bookmarked ? "Bookmark removed" : "Bookmarked", description: "Saved to your browser (MOCK)." });
  };

  return (
    <Card className="border-border/70 bg-background/60 backdrop-blur-[16px]">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between gap-3">
          <Badge variant="secondary" className="bg-secondary/60">{post.category}</Badge>
          <Button type="button" size="icon" variant="outline" className="bg-background/60" onClick={toggle} aria-label="Toggle bookmark">
            <Bookmark className={`h-4 w-4 ${bookmarked ? "text-primary" : "text-muted-foreground"}`} />
          </Button>
        </div>
        <CardTitle className="mt-3 text-lg">{post.title}</CardTitle>
      </CardHeader>
      <CardContent className="text-sm text-muted-foreground">
        {post.excerpt}
        <div className="mt-3 flex items-center gap-2 text-xs">
          <Badge variant="outline" className="bg-background/60">{post.readingMins} min read</Badge>
          <span>{post.date}</span>
        </div>
      </CardContent>
      <CardFooter className="pt-0">
        <Button asChild variant="link" className="px-0 text-primary">
          <Link to={`/insights/${post.slug}`}>
            Read <ArrowRight className="h-4 w-4" />
          </Link>
        </Button>
      </CardFooter>
    </Card>
  );
};

export const DetailPage = ({ sectionOverride }) => {
  const params = useParams();
  const section = sectionOverride || params.section;
  const slug = params.slug;
  const navigate = useNavigate();

  const data = useMemo(() => {
    if (section === "services") return services.find((x) => x.slug === slug);
    if (section === "industries") return industries.find((x) => x.slug === slug);
    if (section === "solutions") return solutions.find((x) => x.slug === slug);
    if (section === "insights") return insights.posts.find((x) => x.slug === slug);
    return null;
  }, [section, slug]);

  if (!data) {
    return (
      <PageShell title="Not found" subtitle="The page you're looking for doesn't exist.">
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={() => navigate(-1)} className="bg-background/60">
            <ArrowLeft className="h-4 w-4" /> Go back
          </Button>
          <Button asChild>
            <Link to="/">Home</Link>
          </Button>
        </div>
      </PageShell>
    );
  }

  if (section === "insights") {
    return (
      <PageShell
        title={data.title}
        subtitle={`${data.category} - ${data.readingMins} min read - ${data.date}`}
        cta={
          <>
            <Button variant="outline" className="bg-background/60" onClick={() => navigate(-1)}>
              <ArrowLeft className="h-4 w-4" /> Back
            </Button>
            <Button asChild>
              <Link to="/contact">Start a Conversation</Link>
            </Button>
          </>
        }
      >
        <Card className="border-border/70 bg-background/60 backdrop-blur-[16px]">
          <CardContent className="py-6 text-sm text-muted-foreground grid gap-4">
            {data.content.map((p) => (
              <p key={p}>{p}</p>
            ))}
            <Separator />
            <div className="text-xs text-muted-foreground">
              Want a deeper version of this playbook applied to your context? <Link className="text-primary hover:text-primary/80" to="/contact">Contact us</Link>.
            </div>
          </CardContent>
        </Card>
      </PageShell>
    );
  }

  return (
    <PageShell
      title={data.title}
      subtitle={data.summary || data.subtitle || data.overview || ""}
      cta={
        <>
          <Button variant="outline" className="bg-background/60" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4" /> Back
          </Button>
          <Button asChild>
            <Link to="/contact">Talk to an Expert</Link>
          </Button>
        </>
      }
    >
      <div className="grid gap-6 lg:grid-cols-12">
        <Card className="lg:col-span-7 border-border/70 bg-background/60 backdrop-blur-[16px]">
          <CardHeader>
            <CardTitle>Overview</CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground">
            <>{data.overview || data.summary || data.subtitle || ""}</>
            {data.highlights?.length ? (
              <div className="mt-4 grid gap-2">
                {data.highlights.map((h) => (
                  <div key={h} className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-primary mt-0.5" />
                    <span>{h}</span>
                  </div>
                ))}
              </div>
            ) : null}
            {data.sections?.length ? (
              <div className="mt-5 grid gap-4">
                {data.sections.map((sec) => (
                  <div key={sec.heading}>
                    <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      {sec.heading}
                    </div>
                    <div className="mt-1 grid gap-1">
                      {sec.body.map((para) => (
                        <p key={para}>{para}</p>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : null}
          </CardContent>
        </Card>

        <Card className="lg:col-span-5 border-border/70 bg-background/60 backdrop-blur-[16px]">
          <CardHeader>
            <CardTitle>Quick actions</CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground grid gap-3">
            <div className="rounded-xl border border-border/60 bg-background/60 p-3">
              <div className="text-xs font-semibold">Next best step</div>
              <div className="mt-1 text-xs text-muted-foreground">A 20-minute discovery call to align outcomes and constraints.</div>
              <Button asChild className="mt-3 w-full">
                <Link to="/contact">Book a conversation</Link>
              </Button>
            </div>
            <div className="rounded-xl border border-border/60 bg-background/60 p-3">
              <div className="text-xs font-semibold">Prefer email?</div>
              <div className="mt-1 text-xs text-muted-foreground">Reach us at {brand.contact.emails[0]}.</div>
              <Button asChild variant="outline" className="mt-3 w-full bg-background/70">
                <a href={`mailto:${brand.contact.emails[0]}`}>Email us</a>
              </Button>
            </div>
            {hasLiveDemoUrl(data.externalUrl) ? (
              <div className="rounded-xl border border-border/60 bg-background/60 p-3">
                <div className="text-xs font-semibold">Live deployment</div>
                <div className="mt-1 text-xs text-muted-foreground">Open the public experience in a new tab.</div>
                <Button asChild variant="outline" className="mt-3 w-full bg-background/70">
                  <a href={data.externalUrl} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center gap-2">
                    Visit live site
                    <ExternalLink className="h-4 w-4" aria-hidden />
                  </a>
                </Button>
              </div>
            ) : null}
          </CardContent>
        </Card>
      </div>

      {/* service-style deep sections */}
      {section === "services" ? (
        <div className="mt-6 grid gap-6 lg:grid-cols-12">
          <Card className="lg:col-span-6 border-border/70 bg-background/60 backdrop-blur-[16px]">
            <CardHeader>
              <CardTitle>Problems we solve</CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-muted-foreground">
              <ul className="grid gap-2">
                {data.problems.map((p) => (
                  <li key={p} className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-primary mt-0.5" />
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>

          <Card className="lg:col-span-6 border-border/70 bg-background/60 backdrop-blur-[16px]">
            <CardHeader>
              <CardTitle>Capabilities</CardTitle>
            </CardHeader>
            <CardContent>
              <Accordion type="single" collapsible className="w-full">
                {data.capabilities.map((c, idx) => (
                  <AccordionItem key={c.title} value={`cap-${idx}`} className="border-border/60">
                    <AccordionTrigger className="text-left">{c.title}</AccordionTrigger>
                    <AccordionContent className="text-muted-foreground">
                      <ul className="grid gap-1">
                        {c.points.map((pt) => (
                          <li key={pt}>- {pt}</li>
                        ))}
                      </ul>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </CardContent>
          </Card>
        </div>
      ) : null}

      {section === "industries" ? (
        <div className="mt-6 grid gap-6 lg:grid-cols-12">
          <Card className="lg:col-span-6 border-border/70 bg-background/60 backdrop-blur-[16px]">
            <CardHeader>
              <CardTitle>Industry challenges</CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-muted-foreground grid gap-2">
              {data.challenges.map((c) => (
                <div key={c} className="flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 text-primary mt-0.5" />
                  <span>{c}</span>
                </div>
              ))}
            </CardContent>
          </Card>
          <Card className="lg:col-span-6 border-border/70 bg-background/60 backdrop-blur-[16px]">
            <CardHeader>
              <CardTitle>Our solutions</CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-muted-foreground grid gap-2">
              {data.solutions.map((s) => (
                <div key={s} className="flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 text-primary mt-0.5" />
                  <span>{s}</span>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      ) : null}

      {section === "solutions" ? (
        <div className="mt-6">
          <Card className="border-border/70 bg-background/60 backdrop-blur-[16px]">
            <CardHeader>
              <CardTitle>What's included</CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-muted-foreground grid gap-2">
              {data.bullets.map((b) => (
                <div key={b} className="flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 text-primary mt-0.5" />
                  <span>{b}</span>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      ) : null}

      <div className="mt-10 rounded-3xl border border-border/70 bg-[radial-gradient(circle_at_25%_20%,rgba(255,145,56,0.18),transparent_55%)] p-1">
        <div className="rounded-[22px] bg-background/70 backdrop-blur-[18px] p-8 lg:p-10">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div>
              <div className="text-xs font-semibold tracking-widest text-primary/90">Next step</div>
              <div className="mt-2 text-2xl font-semibold tracking-tight">Get a tailored plan</div>
              <div className="mt-2 text-sm text-muted-foreground max-w-2xl">
                Share your constraints (timeline, tech stack, compliance) - we'll respond with an approach, risks, and a practical phase plan.
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button asChild size="lg">
                <Link to="/contact">Request a plan</Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="bg-background/60">
                <Link to="/projects">Browse projects</Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </PageShell>
  );
};

export const NotFoundPage = () => (
  <PageShell title="404" subtitle="This page doesn't exist.">
    <div className="flex items-center gap-3">
      <Button asChild>
        <Link to="/">Go home</Link>
      </Button>
      <Button asChild variant="outline" className="bg-background/60">
        <Link to="/contact">Contact</Link>
      </Button>
    </div>
  </PageShell>
);
